import { type AxiosInstance, type AxiosPromise } from 'axios';
import type { Auth } from '@aviata/platform-types';

import { defaultAuthData } from './config';
import type { OtpTokenPayload } from './types';

/**
 * Method to exchange one time token to our user and refresh tokens.
 * Backend sets time-to-live for user token, so we don't need to set it on our own.
 * Default ttl for user token is 1800 sec (30 min)
 */
const authByOtpToken = (
  http: AxiosInstance,
  otpToken: string,
): AxiosPromise<Auth.User.TokenResponse> => {
  return http.post(
    '/freedom-account/auth/exchange-token',
    { one_time_token: otpToken },
    {
      // @ts-expect-error isUserTokenRequest is not expected to be in config's type, but we need to set it for pfm correctly work with user token requests
      isUserTokenRequest: true,
      /**
       * We need to set isTokenRequest so this request is sent before the pfm initialization,
       * since it is a part of the pfm init process
       * @see https://git.aviata.team/frontend/aviatakz-libs/-/blob/master/packages/platform-client/src/core/request-interceptor.ts#L72
       */
      isTokenRequest: true,
      /**
       * We need to set withCredentials for this request since we need to store cross origin cookie
       * @see https://fetch.spec.whatwg.org/#cors-protocol-and-credentials
       */
      withCredentials: true,
    },
  );
};

/**
 * Method to get one time token.
 * ONLY FOR DEVELOPMENT.
 * In staging and production this logic is handled by native app (window.FreedomApp.authorize)
 * */
const devGetOtpToken = (logonHttp: AxiosInstance) => {
  if (import.meta.env.VITE_RUNTIME_ENV === 'production') {
    throw new Error('This function should not be in production');
  }

  const mockAuthPayloadPromise = new Promise<OtpTokenPayload>(resolve => {
    const storageData = localStorage.getItem('freedom:dev-auth');
    if (!storageData) {
      /* eslint-disable no-console */
      console.log(
        '[FreedomDevAuth] Mock auth data for dev auth is not set in "freedom:dev-auth" in localStorage, so default data is used',
      );
      console.log(defaultAuthData);
      /* eslint-enable no-console */

      resolve(defaultAuthData);
    } else {
      const otpTokenPayload = JSON.parse(storageData);
      resolve(otpTokenPayload);
    }
  });

  /**
   * @param mockAuthPayload - Auth data to mock native app API in browser environment. Need to be set in localStorage manually under "freedom:dev-auth" key
   * */
  return mockAuthPayloadPromise
    .then(mockAuthPayload =>
      logonHttp.post('/freedom-account/auth/logon', mockAuthPayload, {
        /**
         * We need to set isTokenRequest so this request is sent before the pfm initialization,
         * since it is a part of the pfm init process
         * @see https://git.aviata.team/frontend/aviatakz-libs/-/blob/master/packages/platform-client/src/core/request-interceptor.ts#L72
         */
        // @ts-expect-error isTokenRequest is not expected to be in config's type
        isTokenRequest: true,
      }),
    )
    .then(({ data }) => data.one_time_token);
};

/**
 * PFM module for freedom app authorization.
 * Intentionally exported separately to prevent DEV module to get into PROD bundles
 */
export function freedomProdAuth(
  http: AxiosInstance,
  authorize: () => Promise<string>,
) {
  return {
    /** Method to init freedom auth process. */
    auth() {
      return authorize().then(otpToken => authByOtpToken(http, otpToken));
    },
  };
}

/**
 * PFM module for development freedom app authorization.
 * Should be use ONLY in DEV
 * Intentionally exported separately to prevent DEV module to get into PROD bundles
 */
export function freedomDevAuth(http: AxiosInstance, logonHttp: AxiosInstance) {
  return {
    /**
     * Method to init freedom auth process in DEV mode.
     * It mocks native app's auth API.
     */
    auth() {
      return devGetOtpToken(logonHttp).then(otpToken =>
        authByOtpToken(http, otpToken),
      );
    },
  };
}
